import { useCallback, useEffect, useRef, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/KpiCard";
import { api, errMsg, money } from "@/lib/api";

const Th = ({ children, right }) => <th className={`py-2 px-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${right ? "text-right" : "text-left"}`}>{children}</th>;

const Ligne = ({ f, reload }) => {
  const [cad, setCad] = useState(f.montant_cad ?? "");
  const saveCad = async () => {
    const v = cad === "" ? null : Number(cad);
    if (v === (f.montant_cad ?? null)) return;
    try { await api.put(`/factures-lodgify/${f.id}`, { montant_cad: v }); toast.success("Montant en $ CA enregistré"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => { try { await api.delete(`/factures-lodgify/${f.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <tr className="border-t border-slate-100" data-testid={`lodgify-row-${f.numero}`}>
      <td className="p-2 font-mono text-xs">{f.numero}</td>
      <td className="p-2 font-mono text-xs whitespace-nowrap">{f.date}</td>
      <td className="p-2 text-xs">{f.description}<div className="text-slate-500">{f.periode}</div></td>
      <td className="p-2 text-right font-mono text-xs whitespace-nowrap">{f.montant.toFixed(2).replace(".", ",")} {f.devise}</td>
      <td className="p-2"><Input type="number" min="0" step="0.01" value={cad} onChange={(e) => setCad(e.target.value)} onBlur={saveCad}
        placeholder="à saisir" className={`h-8 w-28 ml-auto text-right ${f.montant_cad == null ? "border-amber-400" : ""}`} data-testid={`lodgify-cad-${f.numero}`} /></td>
      <td className="p-2 text-right"><Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={del} data-testid={`lodgify-delete-${f.numero}`}><Trash2 className="h-4 w-4" /></Button></td>
    </tr>
  );
};

export const LodgifyFactures = () => {
  const ref = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(() => api.get("/factures-lodgify").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { reload(); }, [reload]);
  const envoyer = async (files) => {
    if (!files.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    setLoading(true);
    try {
      const { data } = await api.post("/factures-lodgify", fd);
      data.forEach((r) => (r.statut === "ok" ? toast.success(`Facture ${r.numero} importée`) : toast.warning(`${r.fichier} : ${r.message}`)));
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); ref.current.value = ""; }
  };
  const manquants = rows.filter((f) => f.montant_cad == null).length;
  return (
    <Panel title="Factures Lodgify (carte de crédit)" testId="lodgify-panel"
      action={<>
        <input ref={ref} type="file" accept=".pdf" multiple className="hidden" onChange={(e) => envoyer(e.target.files)} data-testid="lodgify-file-input" />
        <Button size="sm" onClick={() => ref.current.click()} disabled={loading} className="bg-emerald-700 hover:bg-emerald-800" data-testid="lodgify-import-button">
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}Importer des factures PDF</Button>
      </>}>
      <p className="text-xs text-slate-500 mb-3">Lodgify facture en dollars américains. Saisissez le montant en $ CA de votre relevé Visa : seul ce montant est compté dans les charges du bilan (« Abonnement Lodgify », réparti entre les logements).</p>
      {manquants > 0 && <p className="text-xs text-amber-700 mb-2" data-testid="lodgify-cad-manquants">{manquants} facture{manquants > 1 ? "s" : ""} sans montant en $ CA : non comptée{manquants > 1 ? "s" : ""} dans le bilan.</p>}
      {rows.length === 0 ? <p className="text-sm text-slate-500" data-testid="lodgify-vide">Aucune facture importée.</p> : (
        <div className="overflow-x-auto -mx-5">
          <SmartTable id="lodgify-factures" className="w-full text-sm min-w-[680px]">
            <thead><tr><Th>N° facture</Th><Th>Date</Th><Th>Description · période</Th><Th right>Montant facturé</Th><Th right>Montant $ CA</Th><Th /></tr></thead>
            <tbody>{rows.map((f) => <Ligne key={f.id} f={f} reload={reload} />)}</tbody>
          </SmartTable>
          <p className="px-5 pt-3 text-xs font-mono text-slate-600 text-right" data-testid="lodgify-total-cad">Total en $ CA : {money(rows.reduce((s, f) => s + (f.montant_cad || 0), 0))}</p>
        </div>
      )}
    </Panel>
  );
};
